import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import dayjs from 'dayjs';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Paper,
  Stack,
  Snackbar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  ContentCopy as ContentCopyIcon,
  QrCode as QrCodeIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';

export default function ModalTotemQR({ open, onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [qrDataUrl, setQrDataUrl] = useState('');
  const [currentTime, setCurrentTime] = useState(dayjs().format('HH:mm:ss'));
  const [currentDate, setCurrentDate] = useState(dayjs().format('dddd D [de] MMMM [de] YYYY'));
  const [copied, setCopied] = useState(false);

  const registrationUrl = `${window.location.origin}/asistencia-laboral/registrar`;

  useEffect(() => {
    if (open) {
      QRCode.toDataURL(registrationUrl, {
        width: 320,
        margin: 2,
        color: {
          dark: '#004582',
          light: '#FFFFFF',
        },
      })
        .then((url) => setQrDataUrl(url))
        .catch((err) => console.error(err));
    }
  }, [open, registrationUrl]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(dayjs().format('HH:mm:ss'));
      setCurrentDate(dayjs().format('dddd D [de] MMMM [de] YYYY'));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(registrationUrl);
    setCopied(true);
  };

  const handleDownloadQR = () => {
    const link = document.createElement('a');
    link.download = `QR_Asistencia_Laboral_${dayjs().format('YYYYMMDD')}.png`;
    link.href = qrDataUrl;
    link.click();
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>QR Asistencia Laboral - Gobierno de Córdoba</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              text-align: center;
              padding: 40px;
              background-color: #f8fafc;
            }
            .card {
              border: 3px solid #004582;
              border-radius: 24px;
              padding: 40px;
              display: inline-block;
              background: white;
              box-shadow: 0 8px 30px rgba(0,0,0,0.1);
              max-width: 450px;
            }
            h1 { color: #004582; margin-bottom: 5px; font-size: 24px; }
            h2 { color: #009ada; margin-top: 0; font-size: 18px; }
            p { color: #475569; font-size: 15px; }
            img.qr { width: 280px; height: 280px; margin: 20px 0; border: 1px solid #e2e8f0; border-radius: 12px; }
            .footer { font-size: 12px; color: #94a3b8; margin-top: 20px; }
          </style>
        </head>
        <body>
          <div class="card">
            <h1>GOBIERNO DE CÓRDOBA</h1>
            <h2>Control de Asistencia Laboral</h2>
            <p>Escaneá con tu celular para registrar tu Entrada o Salida diaria.</p>
            <img class="qr" src="${qrDataUrl}" alt="QR Asistencia" />
            <p><strong>Sin necesidad de usuario</strong> · Verificación con CUIL</p>
            <div class="footer">InscribCórdoba — Subsecretaría de Formación y Capacitación</div>
          </div>
          <script>
            window.onload = function() { window.print(); window.close(); }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: 'secondary.main',
            color: 'common.white',
            py: 2,
            px: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <QrCodeIcon sx={{ fontSize: 26 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'common.white' }}>
              Tótem / Código QR de Asistencia
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'common.white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2, sm: 3.5 }, bgcolor: 'background.default', textAlign: 'center' }}>
          {/* Reloj en tiempo real institucional */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 2 },
              mb: 2.5,
              borderRadius: '16px',
              bgcolor: 'common.white',
              border: '1px solid',
              borderColor: 'grey.300',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'secondary.main', mb: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 'bold',
                  letterSpacing: 2,
                  color: 'secondary.main',
                  fontSize: { xs: '1.5rem', sm: '2rem' },
                }}
              >
                {currentTime}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', textTransform: 'capitalize', fontSize: { xs: '0.78rem', sm: '0.875rem' } }}>
              {currentDate}
            </Typography>
          </Paper>

          {/* Tarjeta del Código QR */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: '20px',
              bgcolor: 'common.white',
              border: '2px solid',
              borderColor: 'primary.main',
              display: 'inline-block',
              boxShadow: '0 8px 30px rgba(0, 154, 218, 0.12)',
              mb: 2.5,
              maxWidth: '100%',
            }}
          >
            {qrDataUrl ? (
              <Box
                component="img"
                src={qrDataUrl}
                alt="QR Asistencia Laboral"
                sx={{
                  width: { xs: 200, sm: 260 },
                  height: { xs: 200, sm: 260 },
                  display: 'block',
                  mx: 'auto',
                }}
              />
            ) : (
              <Typography>Generando QR...</Typography>
            )}

            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 'bold',
                color: 'secondary.main',
                mt: 1.5,
                fontSize: { xs: '0.95rem', sm: '1.1rem' },
              }}
            >
              ESCANEAR PARA ASISTENCIA
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: { xs: '0.72rem', sm: '0.8rem' } }}>
              EL personal no necesitan usuario. Sólo ingresar su CUIL.
            </Typography>
          </Paper>

          {/* Enlace directo */}
          <Box
            sx={{
              p: 1.25,
              borderRadius: '12px',
              bgcolor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              maxWidth: 420,
              mx: 'auto',
              border: '1px solid',
              borderColor: 'grey.300',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontFamily: 'monospace',
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontWeight: 600,
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
              }}
            >
              {registrationUrl}
            </Typography>
            <Tooltip title="Copiar enlace">
              <Button
                size="small"
                variant="contained"
                onClick={handleCopyLink}
                startIcon={<ContentCopyIcon fontSize="small" />}
                sx={{
                  fontSize: '0.72rem',
                  py: 0.5,
                  px: 1.5,
                  borderRadius: '8px',
                  bgcolor: 'secondary.main',
                  '&:hover': { bgcolor: 'custom.CBA_Blue_Darker' },
                }}
              >
                Copiar
              </Button>
            </Tooltip>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            p: 2.5,
            bgcolor: 'common.white',
            borderTop: '1px solid',
            borderColor: 'grey.300',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            justifyContent: 'space-between',
          }}
        >
          <Stack direction="row" spacing={1.5} width={{ xs: '100%', sm: 'auto' }}>
            <Button
              variant="outlined"
              fullWidth={isMobile}
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{
                fontWeight: 600,
                borderRadius: '12px',
                borderColor: 'secondary.main',
                color: 'secondary.main',
              }}
            >
              Imprimir QR
            </Button>
            <Button
              variant="outlined"
              fullWidth={isMobile}
              startIcon={<DownloadIcon />}
              onClick={handleDownloadQR}
              sx={{
                fontWeight: 600,
                borderRadius: '12px',
                borderColor: 'primary.main',
                color: 'primary.main',
              }}
            >
              Descargar PNG
            </Button>
          </Stack>

          <Button
            variant="contained"
            fullWidth={isMobile}
            onClick={onClose}
            sx={{
              fontWeight: 600,
              borderRadius: '12px',
              bgcolor: 'secondary.main',
              '&:hover': { bgcolor: 'custom.CBA_Blue_Darker' },
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copied}
        autoHideDuration={2500}
        onClose={() => setCopied(false)}
        message="Enlace de asistencia copiado al portapapeles"
      />
    </>
  );
}
